'use client';

import { Box, ButtonBase, Theme, Typography } from '@mui/material';
import { withClarityTheme } from '@verifime/styles';
import { ReactElement, ReactNode, RefObject, useId, useMemo } from 'react';
import type { TSelectableEntityType } from '../../EntityTypeSelector/EntityTypeSelector/entityTypeGroups';
import type { RequestIdentityState } from '../EntityInitialsAvatar/EntityInitialsAvatar';
import { NewIdentityCard } from '../NewIdentityCard/NewIdentityCard';
import { RequestIdentityCard } from '../RequestIdentityCard/RequestIdentityCard';
import { IDENTITY_TYPE_ROW_COPY } from './identityTypeRowCopy';
import type { RadioItem } from './radioGroupNavigation';
import { arrowOverhang, pinnedClearance } from './arrowGeometry';
import { ScrollArrows, type ScrollArrowsProps } from './ScrollArrows';
import { useRovingRadioGroup } from './useRovingRadioGroup';
import { useWholeCardRow } from './useWholeCardRow';

export type IdentityTypeRowIdentity = {
  id: string;
  name: string;
  state: RequestIdentityState;
};

export type IdentityTypeRowProps = {
  entityType: TSelectableEntityType;
  /** The entity type's name, such as the type tile's title. */
  typeLabel: string;
  description: string;
  /** The entity type's icon, drawn as given. */
  icon: ReactNode;
  /**
   * The identities held of this type, in the order to show them. The row does not sort. Ids must
   * be unique, and none may be `new:<entityType>`: a repeated id shows every card with it as
   * chosen (the group still keeps a single tab stop).
   */
  identities: IdentityTypeRowIdentity[];
  /**
   * The id of the chosen option on the whole screen, or null. It can name a card in another row,
   * in which case nothing here is chosen. The parent owns the choice.
   */
  selectedId: string | null;
  /** Reports the id of a tapped identity, or `new:<entityType>` for the new-one option. */
  onSelect: (id: string) => void;
  /** The held-count text. It is shown only above six identities. */
  heldCountLabel?: (count: number) => string;
  /** The accessible name of the arrow that scrolls the row on, to the right. */
  scrollCueLabel?: string;
  /** The accessible name of the arrow that scrolls the row back, to the left. */
  scrollBackLabel?: string;
  /**
   * `desktop` keeps the new-one card in the row, held at its end when the row overflows.
   * `phone` puts the new-one option under the row.
   */
  layout: 'desktop' | 'phone';
  'data-testid'?: string;
};

/** The id the new-one option reports for an entity type. */
export function toNewIdentityId(entityType: TSelectableEntityType): string {
  return `new:${entityType}`;
}

// The held count shows above this many identities (D13).
const HELD_COUNT_ABOVE = 6;

// The card that shows as chosen: a shared identity never does, whatever `selectedId` says.
function chosenIndexOf(identities: IdentityTypeRowIdentity[], selectedId: string | null) {
  const index = identities.findIndex(({ id, state }) => id === selectedId && state !== 'shared');
  return index >= 0 ? index : null;
}

function toRadioItem({ id, state }: IdentityTypeRowIdentity): RadioItem {
  return { id, unavailable: state === 'shared' };
}

// The box that scrolls the cards sideways. Whole cards only: it snaps each card's left edge to the
// row's left edge. The row sets one width for every card, so the visible cards fill it from edge to
// edge with the minimum gap between them.
const scrollerSx = (theme: Theme) => ({
  display: 'flex',
  alignItems: 'flex-start',
  columnGap: theme.semantic.spacing.sm,
  boxSizing: 'border-box',
  // Fixed row height: the card, its padding above and the room below for the lifted shadow and
  // the Selected pill. The padding is wide enough that a card's focus ring is not cut off by the
  // scrolling box.
  height: `calc(${theme.identityCard.height} + ${theme.semantic.spacing.sm} + ${theme.semantic.spacing['3xs']})`,
  paddingTop: theme.semantic.spacing['2xs'],
  paddingInline: theme.semantic.spacing['2xs'],
  paddingBottom: `calc(${theme.semantic.spacing.sm} - ${theme.semantic.spacing['3xs']})`,
  overflowX: 'auto',
  overflowY: 'hidden',
  // The page's global style makes every scroll smooth, which would also animate the browser's own
  // scroll to a focused card. Only the row's own scrolls animate, and say so.
  scrollBehavior: 'auto',
  scrollSnapType: 'x mandatory',
  scrollPaddingInline: theme.semantic.spacing['2xs'],
  '& > *': { scrollSnapAlign: 'start' },
  // The board draws no scrollbar. A visible one would also eat into the fixed height.
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
});

// The line between the scrolling cards and the pinned new-one card. It is drawn over the cell's
// left padding, so it takes no width and cannot change whether the row overflows. It stands in the
// middle of the clear space between the right arrow's outer edge and the pinned card, so the arrow
// never covers it.
function PinnedDivider(): ReactElement {
  return (
    <Box
      role="separator"
      aria-orientation="vertical"
      sx={(theme: Theme) => ({
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: `calc(${arrowOverhang(theme)} + (${theme.semantic.spacing.sm} - ${theme.semantic.spacing['4xs']}) / 2)`,
        width: theme.semantic.spacing['4xs'],
        backgroundColor: theme.palette.divider,
      })}
    />
  );
}

type HeadProps = {
  titleId: string;
  descriptionId: string;
  icon: ReactNode;
  typeLabel: string;
  description: string;
  identityCount: number;
  heldCountLabel: (count: number) => string;
};

function RowHead({
  titleId,
  descriptionId,
  icon,
  typeLabel,
  description,
  identityCount,
  heldCountLabel,
}: Readonly<HeadProps>): ReactElement {
  const showHeldCount = identityCount > HELD_COUNT_ABOVE;
  return (
    <Box
      sx={(theme: Theme) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: theme.semantic.spacing['2xs'],
      })}
    >
      <Box
        sx={(theme: Theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: theme.semantic.spacing.xs,
        })}
      >
        <Box component="span" aria-hidden="true" sx={{ display: 'inline-flex', flex: 'none' }}>
          {icon}
        </Box>
        <Typography id={titleId} variant="h6" component="span">
          {typeLabel}
        </Typography>
      </Box>
      <Typography id={descriptionId} variant="body2" component="span">
        {description}
      </Typography>
      {showHeldCount && (
        <Typography
          variant="caption"
          component="span"
          sx={(theme: Theme) => ({ color: theme.palette.text.tertiary })}
        >
          {heldCountLabel(identityCount)}
        </Typography>
      )}
    </Box>
  );
}

type ProbeProps = {
  layout: IdentityTypeRowProps['layout'];
  probeRef: RefObject<HTMLDivElement>;
  clearanceRef: RefObject<HTMLDivElement>;
};

// Two unseen boxes the row reads its widths from: one as wide as the narrowest a card may be, and,
// on desktop, one as wide as the room kept clear for the right arrow beside the pinned card.
function MeasuringProbes({ layout, probeRef, clearanceRef }: Readonly<ProbeProps>): ReactElement {
  return (
    <>
      <Box
        ref={probeRef}
        aria-hidden="true"
        sx={(theme: Theme) => ({
          position: 'absolute',
          width: theme.identityCard.width,
          height: 0,
          visibility: 'hidden',
          pointerEvents: 'none',
        })}
      />
      {layout === 'desktop' && (
        <Box
          ref={clearanceRef}
          aria-hidden="true"
          sx={(theme: Theme) => ({
            position: 'absolute',
            width: pinnedClearance(theme),
            height: 0,
            visibility: 'hidden',
            pointerEvents: 'none',
          })}
        />
      )}
    </>
  );
}

type PinnedCellProps = {
  overflowing: boolean;
  reservedRef: RefObject<HTMLDivElement>;
  children: ReactNode;
};

// The cell at the row's end that holds the new-one card on desktop. While the cards overflow it
// also holds the room for the right arrow and the divider before the card.
function PinnedCell({
  overflowing,
  reservedRef,
  children,
}: Readonly<PinnedCellProps>): ReactElement {
  return (
    <Box
      ref={reservedRef}
      sx={(theme: Theme) => ({
        position: 'relative',
        flex: 'none',
        paddingTop: theme.semantic.spacing['2xs'],
        paddingLeft: overflowing
          ? `calc(${arrowOverhang(theme)} + ${theme.semantic.spacing.sm})`
          : `calc(${theme.semantic.spacing.sm} - ${theme.semantic.spacing['2xs']})`,
        // The room is measured less the scroller's padding either side. This keeps the
        // pinned card's right edge as far inside the row as the first card's left edge.
        paddingRight: theme.semantic.spacing['2xs'],
        paddingBottom: `calc(${theme.semantic.spacing.sm} - ${theme.semantic.spacing['3xs']})`,
      })}
    >
      {overflowing && <PinnedDivider />}
      {children}
    </Box>
  );
}

type ScrollFrameProps = {
  overflowing: boolean;
  scrollerRef: RefObject<HTMLDivElement>;
  onScroll: () => void;
  arrows: ScrollArrowsProps;
  children: ReactNode;
};

// The frame around the scrolling box. It is as wide as its cards while they fit. When they do not,
// it takes the room the pinned new-one card leaves, and the scroller shows whole cards in it. The
// arrows sit beside the scroller, not in it, so the scroller cannot clip their outer half.
function ScrollFrame({
  overflowing,
  scrollerRef,
  onScroll,
  arrows,
  children,
}: Readonly<ScrollFrameProps>): ReactElement {
  return (
    <Box sx={{ position: 'relative', minWidth: 0, flex: overflowing ? '1 1 0' : '0 1 auto' }}>
      <Box ref={scrollerRef} onScroll={onScroll} sx={scrollerSx}>
        {children}
      </Box>
      {overflowing && <ScrollArrows {...arrows} />}
    </Box>
  );
}

/**
 * The identities a customer holds of one entity type, as one row of cards that scrolls sideways,
 * with the option to verify a new one of that type. The whole row is one radio group.
 *
 * Keyboard: Tab enters the group at one stop (the chosen option, else the first available one).
 * The arrow keys move focus along the group, wrapping at the ends, and Space or Enter chooses.
 * An already-shared identity takes focus so that it is announced as unavailable, and never
 * reports a choice.
 *
 * Layout (D3): the new-one card follows the last identity while the cards fit. When they do not,
 * only whole cards show, widened to fill the row from edge to edge with the minimum gap between
 * them, the cards scroll a page at a time, and the new-one card stays at the row's end. An arrow on each edge of the row pages it
 * each way, and shows only when there is more in that direction. Which layout applies comes from
 * the browser's own layout (the room, a card's width and the minimum gap, read with a
 * ResizeObserver), never from how many identities there are.
 *
 * Presentational: the row holds no rule about which option should be chosen, and does not sort.
 */
function IdentityTypeRowComponent({
  entityType,
  typeLabel,
  description,
  icon,
  identities,
  selectedId,
  onSelect,
  heldCountLabel = IDENTITY_TYPE_ROW_COPY.heldCount,
  scrollCueLabel = IDENTITY_TYPE_ROW_COPY.scrollCue,
  scrollBackLabel = IDENTITY_TYPE_ROW_COPY.scrollBack,
  layout,
  'data-testid': dataTestId,
}: Readonly<IdentityTypeRowProps>): ReactElement {
  const titleId = useId();
  const descriptionId = useId();
  const {
    rowRef,
    scrollerRef,
    reservedRef,
    probeRef,
    clearanceRef,
    onScroll,
    scrollByPage,
    revealCard,
    overflowing,
    more,
  } = useWholeCardRow({ chosenIndex: chosenIndexOf(identities, selectedId) });

  const newId = toNewIdentityId(entityType);
  const items = useMemo<RadioItem[]>(
    () => [...identities.map(toRadioItem), { id: newId, unavailable: false }],
    [identities, newId],
  );
  const { groupHandlers, tabIndexOf } = useRovingRadioGroup({
    items,
    selectedId,
    onRadioFocus: revealCard,
  });

  const newOne = (variant: 'card' | 'row') => (
    <NewIdentityCard
      entityTypeLabel={typeLabel}
      selected={selectedId === newId}
      onSelect={() => onSelect(newId)}
      variant={variant}
      tabIndex={tabIndexOf(identities.length)}
    />
  );

  return (
    <Box
      data-testid={dataTestId}
      sx={(theme: Theme) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: theme.semantic.spacing.xs,
        minWidth: 0,
      })}
    >
      <RowHead
        titleId={titleId}
        descriptionId={descriptionId}
        icon={icon}
        typeLabel={typeLabel}
        description={description}
        identityCount={identities.length}
        heldCountLabel={heldCountLabel}
      />
      <Box
        role="radiogroup"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        {...groupHandlers}
        sx={(theme: Theme) => ({
          display: 'flex',
          flexDirection: 'column',
          gap: theme.semantic.spacing.xs,
        })}
      >
        <Box
          ref={rowRef}
          sx={(theme: Theme) => ({
            position: 'relative',
            display: 'flex',
            alignItems: 'flex-start',
            // While the cards fit, the room kept clear for the right arrow is empty, at the row's
            // end. Once they overflow it moves to the left of the pinned card, where the arrow is.
            paddingRight: layout === 'desktop' && !overflowing ? pinnedClearance(theme) : 0,
          })}
        >
          <MeasuringProbes layout={layout} probeRef={probeRef} clearanceRef={clearanceRef} />
          <ScrollFrame
            overflowing={overflowing}
            scrollerRef={scrollerRef}
            onScroll={onScroll}
            arrows={{
              showLeft: more.left,
              showRight: more.right,
              leftLabel: scrollBackLabel,
              rightLabel: scrollCueLabel,
              onLeft: () => scrollByPage('left'),
              onRight: () => scrollByPage('right'),
            }}
          >
            {identities.map(({ id, name, state }, index) => (
              <RequestIdentityCard
                key={id}
                id={id}
                name={name}
                entityType={entityType}
                state={state}
                selected={selectedId === id}
                onSelect={onSelect}
                tabIndex={tabIndexOf(index)}
              />
            ))}
          </ScrollFrame>
          {layout === 'desktop' && (
            <PinnedCell overflowing={overflowing} reservedRef={reservedRef}>
              {newOne('card')}
            </PinnedCell>
          )}
        </Box>
        {layout === 'phone' && newOne('row')}
      </Box>
    </Box>
  );
}

export const IdentityTypeRow = withClarityTheme(IdentityTypeRowComponent);
